import { formatAddressPlain } from '@rabbit/sage/utils/helpers';
import { MRT_Row } from 'material-react-table';
import { download, generateCsv } from 'export-to-csv';

/**
 * Converts a value to a primitive type (string, number, boolean, null, undefined)
 * @param value - The value to convert
 * @returns The converted primitive value
 */
export const convertToPrimitive = (
  value: any
): string | number | boolean | null | undefined => {
  if (value === null || value === undefined) {
    return value;
  }

  const type = typeof value;

  if (type === 'string' || type === 'number' || type === 'boolean') {
    return value;
  }

  if (type === 'object') {
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }
    if (value.props && value.props.children) {
      return String(value.props.children);
    }
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }

  // Convert any other types to string
  return String(value);
};

/**
 * Formats a column name for export by replacing underscores with spaces and capitalizing the first letter
 * @param name - The column name to format
 * @returns The formatted column name
 */
export const formatColumnName = (name: string): string => {
  let formatted = name.replace(/_/g, ' ');
  formatted = formatted.replace(/([A-Z])/g, ' $1').trim();
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

/**
 * Process data for export by removing certain fields and formatting others
 * @param data - The data to process
 * @returns The processed data ready for export
 */
const processDataForExport = (data: any[]) =>
  data.map(
    ({
      link,
      timeStamp,
      purchaseDate,
      expiring,
      registered,
      status,
      start,
      expires,
      id,
      in_warranty,
      edit,
      spotlight,
      date_modified,
      local,
      cloudsaved,
      docid,
      actions,
      error,
      version,
      lastTouched,
      shippingAddress,
      ...rest
    }: any) => {
      const processedRest: Record<string, any> = {};
      Object.entries(rest).forEach(([key, value]) => {
        if (
          !key.toLowerCase().includes('time stamp') &&
          !key.toLowerCase().includes('edit') &&
          !key.toLowerCase().includes('timestamp') &&
          !key.toLowerCase().includes('spotlight') &&
          !key.toLowerCase().includes('docid') &&
          !key.toLowerCase().includes('image url') &&
          !key.toLowerCase().includes('actions')
        ) {
          processedRest[key] = convertToPrimitive(value);
        }
      });
      return {
        ...processedRest,
        ...(processedRest['Purchase Date'] && {
          'Purchase Date': new Date(
            processedRest['Purchase Date']
          ).toLocaleDateString(),
        }),
        ...(processedRest['Expiring'] && {
          Expiring: new Date(processedRest['Expiring']).toLocaleDateString(),
        }),
        ...(processedRest['Registered'] && {
          Registered: new Date(
            processedRest['Registered']
          ).toLocaleDateString(),
        }),
        ...(processedRest['Start'] && {
          Start: new Date(processedRest['Start']).toLocaleDateString(),
        }),
        ...(processedRest['Expires'] && {
          Expires: new Date(processedRest['Expires']).toLocaleDateString(),
        }),
        ...(date_modified && {
          'Date Modified': new Date(date_modified).toLocaleDateString(),
        }),
        ...(processedRest['Id'] && {
          Id: (() => {
            try {
              const parsedId = JSON.parse(processedRest['Id']);
              return parsedId[1].slice(0, 5).toUpperCase();
            } catch (e) {
              return processedRest['Id'].slice(0, 5).toUpperCase();
            }
          })(),
        }),
        // Removed lastTouched (timestamp) field
        ...(status && { Status: convertToPrimitive(status) }),
        ...(in_warranty && {
          'In Warranty': convertToPrimitive(in_warranty),
        }),
        ...(processedRest['Shipping Address'] && {
          'Shipping Address': processedRest['Shipping Address']
            ? formatAddressPlain(
                JSON.parse(processedRest['Shipping Address'])[0]
              )
            : '',
        }),
      };
    }
  );

/**
 * Helper function to prepare data for export by filtering columns and formatting column names
 * @param data - The data to prepare
 * @param columnIds - The column IDs to include
 * @returns The prepared data
 */
export const prepareDataForExport = (
  data: Record<string, any>[],
  columnIds: string[]
): Record<string, any>[] => {
  return data.map((item: Record<string, any>) => {
    const filteredItem: Record<string, any> = {};
    columnIds.forEach((colId) => {
      if (item[colId] !== undefined) {
        filteredItem[formatColumnName(colId)] = item[colId];
      }
    });
    return filteredItem;
  });
};

/**
 * Export selected rows to CSV
 * @param rows - The rows to export
 * @param table - The table instance
 * @param csvConfig - The CSV configuration
 * @param handleClose - Function to close the export menu
 */
export const exportRows = (
  rows: MRT_Row<any>[],
  table: any,
  csvConfig: any,
  handleClose: () => void
) => {
  const visibleColumns = table.getVisibleLeafColumns();
  const columnIds = visibleColumns.map((col: any) => col.id);
  const rowData = rows.map((row) => row.original);

  const filteredData = prepareDataForExport(rowData, columnIds);
  const dataImport = processDataForExport(filteredData);

  handleClose();

  const csv = generateCsv(csvConfig)(dataImport);
  download(csvConfig)(csv);
};

/**
 * Export all data to CSV
 * @param data - The data to export
 * @param table - The table instance
 * @param csvConfig - The CSV configuration
 * @param handleClose - Function to close the export menu
 */
export const exportAllData = (
  data: Record<string, any>[],
  table: any,
  csvConfig: any,
  handleClose: () => void
) => {
  const visibleColumns = table.getVisibleLeafColumns();
  const columnIds = visibleColumns.map((col: any) => col.id);

  const filteredData = prepareDataForExport(data, columnIds);
  const dataImport = processDataForExport(filteredData);

  handleClose();

  const csv = generateCsv(csvConfig)(dataImport);
  download(csvConfig)(csv);
};

/**
 * Export filtered rows to CSV
 * @param table - The table instance
 * @param csvConfig - The CSV configuration
 * @param handleClose - Function to close the export menu
 */
export const exportFilteredRows = (
  table: any,
  csvConfig: any,
  handleClose: () => void
) => {
  const visibleColumns = table.getVisibleLeafColumns();
  const columnIds = visibleColumns.map((col: any) => col.id);
  const rowData = table
    .getFilteredRowModel()
    .rows.map((row: any) => row.original);

  const filteredData = prepareDataForExport(rowData, columnIds);
  const dataImport = processDataForExport(filteredData);

  handleClose();

  const csv = generateCsv(csvConfig)(dataImport);
  download(csvConfig)(csv);
};
